import { Form, Formik } from "formik";
import { useEffect, useState } from "react";
import * as Yup from "yup";
import { ICCheckbox } from "../../core-component/form";
import { ICFormikDropDown, ICFormikTextInput } from "../../core-component/formik";
import { ICButton, ICModal } from "../../core-component/ui";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import { DUPLICATE_SUBSCRIPTION_PACKAGE } from "../../services/Endpoints";
import {
  DEFAULT_STATUS_CODE_SUCCESS,
  MESSAGE_UNKNOWN_ERROR_OCCURRED,
} from "../../utils/AppConstants";
import { handleError, prepareMessageFromParams, toastSuccess } from "../../utils/AppFunctions";
import {
  getPlanTypeLabel,
  getVisibilityTypeOptions,
  PURCHASE_NAME_MAX_LENGTH,
} from "../../utils/PurchaseConstants";
import { VISIBILITY_TYPE_ENUM } from "../../utils/PurchaseEnumerations";
import {
  getPackageDuplicateSources,
  IPackageDuplicateSource,
} from "./SubscriptionPackageViewController";

interface IPackageDuplicateModalProps {
  defaultSource?: IPackageDuplicateSource;
  onClose: () => void;
  onCreated: (newPackageId: string) => void;
}

interface IDuplicateForm {
  source_id: string;
  name: string;
  visibility_type: string;
  has_desktop: boolean;
}

const PackageDuplicateModal: React.FC<IPackageDuplicateModalProps> = ({
  defaultSource,
  onClose,
  onCreated,
}) => {
  const { t } = UseTranslationHook();
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState("");
  const [sources, setSources] = useState<IPackageDuplicateSource[]>(
    defaultSource ? [defaultSource] : [],
  );

  useEffect(() => {
    getPackageDuplicateSources(setSources);
  }, []);

  const sourceOptions = sources.map((item) => ({
    data_value: item.id,
    display_value: `${item.name} (${getPlanTypeLabel(t, item.platforms)})`,
  }));

  const requiredMessage = (fieldName: string) =>
    prepareMessageFromParams(t("error_message_required"), [["fieldName", fieldName]]);

  const handleSubmit = async (values: IDuplicateForm) => {
    setLoading(true);
    setServerError("");
    try {
      const response = await DUPLICATE_SUBSCRIPTION_PACKAGE(values.source_id, {
        name: values.name.trim(),
        visibility_type: values.visibility_type,
        has_desktop: values.has_desktop,
      });
      if (response?.code === DEFAULT_STATUS_CODE_SUCCESS && response.data?.id) {
        toastSuccess(response.message);
        onCreated(response.data.id);
        return;
      }
      setServerError(response?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED);
    } catch (error) {
      handleError(error, "duplicateSubscriptionPackage");
    } finally {
      setLoading(false);
    }
  };

  return (
    <ICModal title={t("create_package_from_copy")} onClose={onClose}>
      <Formik
        initialValues={{
          source_id: defaultSource?.id ?? "",
          name: "",
          visibility_type:
            defaultSource && defaultSource.visibility_type !== VISIBILITY_TYPE_ENUM.FreeTrial
              ? defaultSource.visibility_type
              : VISIBILITY_TYPE_ENUM.Private,
          has_desktop: defaultSource?.has_desktop ?? false,
        }}
        validationSchema={Yup.object().shape({
          source_id: Yup.string().required(requiredMessage(t("copy_from"))),
          name: Yup.string()
            .trim()
            .required(requiredMessage(t("new_package_name")))
            .max(
              PURCHASE_NAME_MAX_LENGTH,
              prepareMessageFromParams(t("error_message_max_length"), [
                ["fieldName", t("new_package_name")],
                ["max", String(PURCHASE_NAME_MAX_LENGTH)],
              ]),
            ),
          visibility_type: Yup.string().required(requiredMessage(t("type"))),
        })}
        onSubmit={handleSubmit}
      >
        {(formikProps) => (
          <Form onSubmit={formikProps.handleSubmit} className="flex flex-col gap-4 overflow-y-scroll">
            <div className="text-sm text-muted">{t("copy_package_hint")}</div>
            <ICFormikDropDown
              {...formikProps}
              name="source_id"
              placeholder={t("copy_from")}
              options={sourceOptions}
              searchable
              required
            />
            <div>
              <ICFormikTextInput
                {...formikProps}
                fieldName="name"
                placeholder={t("new_package_name")}
                required
                onChangeText={(text) => {
                  setServerError("");
                  formikProps.setFieldValue("name", text);
                }}
              />
              {serverError && <div className="text-sm text-danger mt-1">{serverError}</div>}
            </div>
            <ICFormikDropDown
              {...formikProps}
              name="visibility_type"
              placeholder={t("type")}
              options={getVisibilityTypeOptions(t, false)}
              required
            />
            <ICCheckbox label={t("platform_pwa_mobile_always")} checked disabled onChange={() => {}} />
            <ICCheckbox
              label={t("platform_desktop")}
              checked={formikProps.values.has_desktop}
              onChange={(checked) => {
                setServerError("");
                formikProps.setFieldValue("has_desktop", checked);
              }}
            />
            <ICButton loading={loading} className="w-full">
              {t("submit")}
            </ICButton>
          </Form>
        )}
      </Formik>
    </ICModal>
  );
};

export default PackageDuplicateModal;
